import { EventEmitter, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ShopSettingsService } from '../../general/services/shop-settings.service';
import { switchMap, catchError } from 'rxjs/operators';
import { timer, Subscription, of } from 'rxjs';
import { BookingOrderService } from './booking-order.service';
import { BookingService } from './booking.service';
import { LoggerService } from '../../../services/logger/logger.service';

@Injectable()
export class BookingCapacityService {

  readonly messageChange: EventEmitter<any> = new EventEmitter<any>();
  private _loading: boolean;
  private _isEnabled: boolean;
  private requestDelayTimeout = 1000 * 60 * 5;
  private _availableTicketQuantity: number;

  private subscription: Subscription;

  constructor(
    private translateService: TranslateService,
    private shopSettingsService: ShopSettingsService,
    private bookingOrderService: BookingOrderService,
    private bookingService: BookingService,
    private loggerService: LoggerService,
  ) {
  }

  get isEnabled(): boolean {
    return this._isEnabled;
  }

  start() {
    this._loading = true;
    this._isEnabled = this.isShowQuantityAvailableTicketEnabled;

    if (!this._isEnabled) {
      return;
    }

    this.subscription = timer(0, this.requestDelayTimeout)
      .pipe(
        switchMap(
          () => this.bookingService.getAvailableTicketQuantity()
            .pipe(
              catchError((error) => {
                this.loggerService.error(error);
                return of(0);
              })
            )
        )
      )
      .subscribe(
        (quantity: number) => {
          this._availableTicketQuantity = quantity;
          this._loading = false;
          this.messageChange.emit();
        },
        error => {
          this.loggerService.error(error);
        }
      );
  }

  stop() {
    this._isEnabled = false;
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  get isShowQuantityAvailableTicketEnabled(): boolean {
    return this.shopSettingsService.isVUMode && this.bookingOrderService.showQuantityAvailableTicketsOnMainPage;
  }

  get availableTicketQuantityMessage(): string {
    return this.buildAvailableTicketQuantityMessage(this._availableTicketQuantity);
  }

  buildAvailableTicketQuantityMessage(quantity: number) {

    if (this._loading) {
      return this.translateService.instant('Available Tickets') + ': ' + this.translateService.instant('Loading..');
    }

    if (quantity <= 0) {
      return this.translateService.instant('No available Tickets');
    }

    return this.translateService.instant('Available Tickets') + ': ' + quantity;
  }
}
